import React from 'react';
import { Link } from 'react-router-dom';
import { Loader } from '../../common/Loader';
import NetworkedHubCard from './components/NetworkedHubCard';
import Stepper from './components/Stepper';
import StepPanel from './components/StepPanel';
import { useNetworkPlusCommunity } from './hooks/useNetworkPlusCommunity';
import { useNetworkPlusSteps } from './hooks/useNetworkPlusSteps';
import './network-plus-setup.css';

interface INetworkPlusSetupProps {
	match: { params: { id: string } };
}

const NetworkPlusSetup: React.FC<INetworkPlusSetupProps> = ({ match }) => {
	const communityId = match?.params?.id;

	const { community, communityName, loading, notFound, refetch } = useNetworkPlusCommunity(communityId);
	const { enabled, activeStepId, steps, toggleEnabled, selectStep } = useNetworkPlusSteps(community);

	const activeStep = steps.find(s => s.id === activeStepId) ?? steps[0];
	const customDomain = community?.customDomain?.domain ?? null;
	const posthogApiKey = community?.posthogApiKey ?? null;
	const isNetworkedHub = community?.isNetworkedHub === true;

	if (loading) {
		return (
			<div className="content-wrapper">
				<section className="content d-flex align-items-center justify-content-center" style={{ minHeight: 300 }}>
					<Loader />
				</section>
			</div>
		);
	}

	if (notFound) {
		return (
			<div className="content-wrapper">
				<section className="content-header">
					<div className="container-fluid">
						<div className="row mb-2">
							<div className="col-sm-6">
								<h1>Network Plus</h1>
							</div>
						</div>
					</div>
				</section>
				<section className="content">
					<div className="row">
						<div className="col-md-8 offset-md-2">
							<div className="alert alert-danger">
								<i className="fas fa-exclamation-triangle mr-2"></i>
								Network Plus setup is only available for top-level communities (layer 0).
								<br />
								<Link to="/communities" className="alert-link">Return to communities list</Link>
							</div>
						</div>
					</div>
				</section>
			</div>
		);
	}

	return (
		<div className="content-wrapper">
			{/* ── Page header ── */}
			<section className="content-header">
				<div className="container-fluid">
					<div className="row mb-2">
						<div className="col-sm-7">
							<h1>
								Network Plus Setup
								{communityName && (
									<small className="text-muted ml-2" style={{ fontSize: '0.6em' }}>
										— {communityName}
									</small>
								)}
							</h1>
						</div>
						<div className="col-sm-5">
							<ol className="breadcrumb float-sm-right">
								<li className="breadcrumb-item"><Link to="/home">Home</Link></li>
								<li className="breadcrumb-item"><Link to="/communities">Communities</Link></li>
								<li className="breadcrumb-item">
									<Link to={`/community/${communityId}`}>Edit Community</Link>
								</li>
								<li className="breadcrumb-item active">Network Plus</li>
							</ol>
						</div>
					</div>
				</div>
			</section>

			<section className="content">
				<div className="row">
					<div className="col-md-12">

						{/* ── Prerequisites block ── */}
						<div className="card card-warning np-disclaimer mb-3">
							<div className="card-header">
								<h3 className="card-title">
									<i className="fas fa-exclamation-circle mr-2"></i>
									Prerequisites — Read before enabling Network Plus
								</h3>
								<div className="card-tools">
									<button type="button" className="btn btn-tool" data-card-widget="collapse" title="Collapse">
										<i className="fas fa-minus"></i>
									</button>
								</div>
							</div>
							<div className="card-body">
								<div className="alert alert-warning mb-3">
									<h6 className="alert-heading mb-2">
										<i className="fas fa-code-branch mr-1"></i>
										Branch Account &amp; App ID Required
									</h6>
									<ul className="mb-0">
										<li>
											To enable Network Plus for a community, you must first have a <strong>Branch account</strong> set up for the client.
										</li>
										<li>
											You also need the <strong>App ID of the new application</strong> that is being built specifically for this client — Branch generates this App ID when you create the app inside their dashboard.
										</li>
										<li>
											Do not proceed without both the Branch account credentials and the App ID confirmed with the client.
										</li>
									</ul>
								</div>
								<div className="alert alert-info mb-0">
									<h6 className="alert-heading mb-2">
										<i className="fas fa-globe mr-1"></i>
										Custom Domain For Supabase &amp; OneSignal
									</h6>
									<ul className="mb-0">
										<li>
											A <strong>custom domain must be enabled and verified</strong> for this community before you can initialise Supabase or OneSignal.
										</li>
										<li>
											<strong>Supabase</strong> is the authentication and database backend for this Network Plus community — it powers sign-in and authenticated user flows.
										</li>
										<li>
											<strong>OneSignal</strong> handles push notifications for authenticated users on the community's Network Plus app — it too requires a working custom domain to be provisioned correctly.
										</li>
										<li>
											Attempting to initialise either service without a custom domain will result in misconfigured environments that are difficult to correct later.
										</li>
										<li>
											<strong>PostHog Analytics</strong> is configured separately and only requires the community's PostHog API key.
										</li>
									</ul>
								</div>
							</div>
						</div>

						{/* ── Enable / disable toggle ── */}
						<div className="card mb-3">
							<div className="card-body py-3">
								<div className="np-toggle-row">
									<div className="custom-control custom-switch">
										<input
											type="checkbox"
											className="custom-control-input"
											id="npEnableSwitch"
											checked={enabled}
											onChange={toggleEnabled}
										/>
										<label className="custom-control-label" htmlFor="npEnableSwitch">
											{enabled ? 'Network Plus is enabled for this community' : 'Enable Network Plus for this community'}
										</label>
									</div>
									{!enabled && (
										<span className="text-muted small ml-2">
											Toggle on to view and configure the setup steps.
										</span>
									)}
								</div>
							</div>
						</div>

						<NetworkedHubCard
							communityId={communityId}
							isNetworkedHub={isNetworkedHub}
							onRefetch={refetch}
						/>

						{/* ── Setup stepper + step panels ── */}
						{enabled && (
							<div className="card card-primary">
								<div className="card-header">
									<h3 className="card-title">
										<i className="fas fa-tasks mr-2"></i>
										Configuration Progress
									</h3>
									<div className="card-tools">
										<button type="button" className="btn btn-tool" data-card-widget="collapse" title="Collapse">
											<i className="fas fa-minus"></i>
										</button>
									</div>
								</div>
								<div className="card-body">
									<p className="text-muted mb-4" style={{ fontSize: '0.9rem' }}>
										Click any unlocked option below to view its configuration details. Custom Domain unlocks Supabase and OneSignal; PostHog Analytics can be configured independently.
									</p>

									<Stepper
										steps={steps}
										activeStepId={activeStepId}
										onSelectStep={selectStep}
									/>

									<StepPanel
										step={activeStep}
										communityId={communityId}
										customDomain={customDomain}
										branchDetails={community?.branchDetails}
										posthogApiKey={posthogApiKey}
										onStepCompleted={() => {}}
										onRefetch={refetch}
									/>
								</div>
							</div>
						)}

					</div>
				</div>
			</section>
		</div>
	);
};

export { NetworkPlusSetup };
